import React, { useEffect, useState } from 'react';
import { Package, MapPin, Flag, Eye, Tag, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { money } from '../lib/constants';
import { PageHeader, Card, Table, Tr, Td, StatusBadge, Avatar, FilterChips, Stat, SimpleBadge } from './shared';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EvidenceGallery } from '../components/DeliveryEvidence';
import { OrderPayment } from '../components/OrderPayment';

const M_STATUS = { pending: 'Buscando conductor', on_way: 'En camino', completed: 'Completado', cancelled: 'Cancelado' };
const PRICE_TONE = { pending: 'amber', accepted: 'green', rejected: 'red', expired: 'gray' };
const PRICE_LABEL = { pending: 'Esperando respuesta del cliente', accepted: 'Precio aceptado', rejected: 'Precio rechazado', expired: 'Expiró sin respuesta' };

const useCountdown = (expiresAt, active) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!active || !expiresAt) return undefined;
    const tick = () => setLeft(Math.max(0, Math.round((new Date(expiresAt) - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt, active]);
  return left;
};

const AdminMandaditos = () => {
  const { mandaditos, drivers, updateOrder, proposePrice } = useApp();
  const [filter, setFilter] = useState('all');
  const [sel, setSel] = useState(null);
  const [fee, setFee] = useState('');
  const rows = mandaditos.filter((m) => filter === 'all' || m.status === filter).slice(0, 100);
  const current = sel ? mandaditos.find((m) => m.id === sel) : null;
  const approved = drivers.filter((d) => d.approval === 'approved' && d.status === 'active');
  const monthRevenue = mandaditos.filter((m) => m.status === 'completed').reduce((s, m) => s + (m.deliveryFee || 0), 0);
  const pendingPrice = current?.priceApproval?.status === 'pending';
  const left = useCountdown(current?.priceApproval?.expiresAt, pendingPrice);
  const sendProposal = () => {
    const n = Number(fee);
    if (!n || n <= 0) return toast.error('Ingresa una tarifa válida');
    proposePrice(current.id, n).then(() => setFee(''));
  };

  return (
    <div>
      <PageHeader title="Mandaditos 7" subtitle="Solicitudes abiertas de compras, recogidas y envíos" />
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <Stat label="Total" value={mandaditos.length} icon={Package} bg="bg-[#FFF6E0]" iconBg="bg-[#FFE8B8] text-[#F08A00]" />
        <Stat label="Buscando conductor" value={mandaditos.filter((m) => m.status === 'pending').length} icon={Flag} bg="bg-[#E8F0FE]" iconBg="bg-[#D2E1FC] text-[#2F80ED]" />
        <Stat label="En camino" value={mandaditos.filter((m) => m.status === 'on_way').length} icon={MapPin} bg="bg-[#E6F7F1]" iconBg="bg-[#CDEFE0] text-[#22A45D]" />
        <Stat label="Ingresos (L)" value={money(monthRevenue)} icon={Package} bg="bg-[#F1ECFD]" iconBg="bg-[#E2D8FB] text-[#7C3AED]" />
      </div>
      <Card>
        <div className="p-4 border-b border-gray-100">
          <FilterChips value={filter} onChange={setFilter} options={[{ value: 'all', label: 'Todos' }, { value: 'pending', label: 'Buscando conductor' }, { value: 'on_way', label: 'En camino' }, { value: 'completed', label: 'Completados' }, { value: 'cancelled', label: 'Cancelados' }]} />
        </div>
        <div className="p-3">
          <Table columns={['# Mandadito', 'Cliente', 'Descripción', 'Recoger en', 'Entregar en', 'Tarifa', 'Estado', 'Conductor', 'Fecha', '']} rows={rows}
            renderRow={(m) => (
              <Tr key={m.id} data-testid={`mandadito-row-${m.id}`}>
                <Td className="font-semibold">{m.id}</Td>
                <Td><span className="flex items-center gap-2"><Avatar src={m.avatar} name={m.customer} size={30} />{m.customer}</span></Td>
                <Td className="max-w-[220px] truncate text-gray-700">{m.description}</Td>
                <Td className="text-gray-600 max-w-[180px] truncate">{m.pickup}</Td>
                <Td className="text-gray-600 max-w-[180px] truncate">{m.dropoff}</Td>
                <Td className="font-semibold">{money(m.deliveryFee)}</Td>
                <Td><StatusBadge status={m.status} /></Td>
                <Td className="text-gray-600">{m.driver || <span className="text-gray-400 italic">Sin asignar</span>}</Td>
                <Td className="text-gray-600 whitespace-nowrap">{m.date} {m.time}</Td>
                <Td><button onClick={() => setSel(m.id)} data-testid={`view-mandadito-${m.id}`} className="h-8 px-3 rounded-lg border border-gray-200 text-xs font-semibold flex items-center gap-1.5 hover:bg-gray-50"><Eye size={14} /> Ver</button></Td>
              </Tr>
            )} />
        </div>
      </Card>

      <Sheet open={!!current} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {current && (<>
            <SheetHeader><SheetTitle className="text-xl font-extrabold">Mandadito {current.id}</SheetTitle></SheetHeader>
            <div className="mt-4 space-y-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3"><Avatar src={current.avatar} name={current.customer} size={44} /><div><p className="font-bold">{current.customer}</p><p className="text-xs text-gray-500">{current.date} {current.time}</p></div></div>
                <StatusBadge status={current.status} />
              </div>
              <div className="bg-[#F7F8FA] rounded-xl p-4 text-sm"><p className="text-xs font-semibold text-gray-500 mb-1">Solicitud</p><p>{current.description}</p></div>
              {!!current.attachments?.length && (
                <div data-testid="mandadito-attachments" className="flex gap-2 overflow-x-auto">
                  {current.attachments.map((url, i) => <a key={i} href={url} target="_blank" rel="noreferrer"><img src={url} alt="" className="w-16 h-16 rounded-lg object-cover border border-gray-200" /></a>)}
                </div>
              )}
              <div className="relative pl-6 space-y-4 text-sm">
                <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-gray-200" />
                <div className="relative"><span className="absolute -left-6 top-0.5 w-4 h-4 rounded-full bg-emerald-500 border-4 border-emerald-100" /><p className="text-xs text-gray-500">Recoger en</p><p className="font-semibold">{current.pickup}</p></div>
                <div className="relative"><span className="absolute -left-6 top-0.5 w-4 h-4 rounded-full bg-[#EE3B36] border-4 border-red-100" /><p className="text-xs text-gray-500">Entregar en</p><p className="font-semibold">{current.dropoff}</p></div>
              </div>
              <div className="flex justify-between font-bold text-base border-t pt-3"><span>Tarifa del servicio</span><span>{money(current.deliveryFee)}</span></div>
              {current.priceApproval && (
                <div data-testid="price-approval-status" className="rounded-xl border border-gray-200 p-3 flex items-center justify-between gap-2">
                  <div><p className="text-xs font-semibold text-gray-500">Propuesta de precio: {money(current.priceApproval.fee)}</p><SimpleBadge tone={PRICE_TONE[current.priceApproval.status]}>{PRICE_LABEL[current.priceApproval.status]}</SimpleBadge></div>
                  {pendingPrice && <span data-testid="price-approval-countdown" className="text-sm font-bold text-amber-600 flex items-center gap-1"><Clock size={14} /> {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}</span>}
                </div>
              )}
              {['pending', 'on_way'].includes(current.status) && !pendingPrice && (
                <div className="rounded-xl border border-dashed border-gray-200 p-3">
                  <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5"><Tag size={13} /> Proponer nuevo precio (el cliente tiene 2 min para responder)</p>
                  <div className="flex gap-2"><Input data-testid="propose-price-input" type="number" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="Nueva tarifa (L)" className="h-9" /><Button data-testid="propose-price-submit" variant="outline" className="h-9" onClick={sendProposal}>Enviar</Button></div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs font-semibold text-gray-500 mb-1.5">Estado</p>
                  <Select value={current.status} onValueChange={(v) => updateOrder(current.id, { status: v }).then(() => toast.success('Estado actualizado'))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(M_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><p className="text-xs font-semibold text-gray-500 mb-1.5">Conductor</p>
                  <Select value={current.driverId || 'none'} onValueChange={(v) => updateOrder(current.id, { driverId: v === 'none' ? null : v }).then(() => toast.success('Conductor actualizado'))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Sin asignar</SelectItem>{approved.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <Button variant="outline" className="w-full" onClick={() => setSel(null)}>Cerrar</Button>
              <OrderPayment order={current} testId="admin-mandadito-payment" />
              <EvidenceGallery order={current} testId="admin-mandadito-evidence" />
            </div>
          </>)}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdminMandaditos;
